/**
 * ProvidersFilters Component
 *
 * Inline filter controls for provider analytics
 */

import React, { useCallback, useMemo } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import {
  Box,
  Chip,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { fromPickerDate, parseCalendarDate, toPickerDate } from '@shared/dates/calendarDate';

export interface ProvidersFilters {
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Period in days (alternative to date range) */
  days?: number;
  /** Completion status filter */
  completionStatus?: 'all' | 'completed' | 'draft';
}

interface ProvidersFiltersProps {
  /** Current filter values */
  filters: ProvidersFilters;
  /** Callback when filters change */
  onFiltersChange: (filters: ProvidersFilters) => void;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Whether to show completion status filter (for provider detail page) */
  showCompletionStatus?: boolean;
}

export const ProvidersFilters: React.FC<ProvidersFiltersProps> = ({
  filters,
  onFiltersChange,
  isLoading = false,
  showCompletionStatus = false,
}) => {
  const { jotauditDashboard } = useEventTracking();

  const handleDateRangeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      const previousValue = filters.dateFrom
        ? `${filters.dateFrom}_${filters.dateTo}`
        : filters.days?.toString() || 'none';
      const newValue = range.start
        ? `${fromPickerDate(range.start)}_${fromPickerDate(range.end)}`
        : 'none';

      // Track filter change
      jotauditDashboard.providersFilterChanged({
        filter_type: 'date_range',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        dateFrom: fromPickerDate(range.start) ?? undefined,
        dateTo: fromPickerDate(range.end) ?? undefined,
        days: undefined, // Clear days when using date range
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleDaysChange = useCallback(
    (days: number | undefined) => {
      const previousValue = filters.days?.toString() || 'none';
      const newValue = days?.toString() || 'none';

      // Track filter change
      jotauditDashboard.providersFilterChanged({
        filter_type: 'days',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        days,
        dateFrom: undefined, // Clear date range when using days
        dateTo: undefined,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleCompletionStatusChange = useCallback(
    (event: SelectChangeEvent) => {
      const previousValue = filters.completionStatus || 'all';
      const newValue = event.target.value as 'all' | 'completed' | 'draft';

      // Track filter change (for provider detail page)
      jotauditDashboard.providerNotesFiltered({
        filter_type: 'completion_status',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        completionStatus: newValue,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const dateRangeValue = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return {
        start: toPickerDate(parseCalendarDate(filters.dateFrom)),
        end: toPickerDate(parseCalendarDate(filters.dateTo)),
      };
    }
    return { start: null, end: null };
  }, [filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = useMemo(() => {
    const hasCompletionFilter = showCompletionStatus && filters.completionStatus !== 'all';
    return !!(
      filters.dateFrom ||
      filters.dateTo ||
      filters.days ||
      hasCompletionFilter
    );
  }, [filters, showCompletionStatus]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Date Range Selector */}
        <Box sx={{ minWidth: 250 }}>
          <DateRangeSelector
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            label="Date Range"
            size="small"
            disabled={isLoading}
            fullWidth
          />
        </Box>

        {/* Days Preset Chips */}
        {!filters.dateFrom && !filters.dateTo && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Quick:
            </Typography>
            {[7, 30, 90].map(days => (
              <Chip
                key={days}
                label={`${days}d`}
                onClick={() => handleDaysChange(filters.days === days ? undefined : days)}
                color={filters.days === days ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}

        {/* Completion Status Select - Only show on provider detail page */}
        {showCompletionStatus && (
          <Box sx={{ minWidth: 150 }}>
            <Select
              value={filters.completionStatus || 'all'}
              onChange={handleCompletionStatusChange}
              size="small"
              disabled={isLoading}
              fullWidth
            >
              <MenuItem value="all">All Notes</MenuItem>
              <MenuItem value="completed">Signed</MenuItem>
              <MenuItem value="draft">Drafts</MenuItem>
            </Select>
          </Box>
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Chip
            label="Clear"
            onClick={() =>
              onFiltersChange({
                dateFrom: undefined,
                dateTo: undefined,
                days: undefined,
                completionStatus: 'all',
              })
            }
            size="small"
            sx={{ cursor: 'pointer', ml: 'auto' }}
          />
        )}
      </Box>
    </Box>
  );
};

